// ドラッグ移動・複製の最終位置決定ロジック（純粋関数）。
//
// 生の移動量から、キャンバス clamp・整列スナップ・（必要なら）グリッド吸着を適用した各フレームの
// 位置と、表示すべき補助線を求める。プレビューと確定で同じ計算を共有し「見た目」と「確定値」を
// 一致させる。React / DOM / store へ依存しないためテストしやすい。
//
// グリッド吸着は **グループ全体へ共通の補正を 1 回だけ** 適用する（外接矩形の左上を CANVAS_SNAP_PX
// グリッドへ揃える）。各オブジェクトを個別に丸めると、グリッドに乗っていない複数選択の相対配置が
// 崩れるため、貼り付け経路（canvas-stage.tsx）と同じく相対配置を保つ。整列が成立した軸はグリッドより
// 整列を優先する。

import { CANVAS_SNAP_PX } from "./canvas-coordinates";
import { computeAlignmentSnap, type AlignmentBox, type AlignmentGuide } from "./canvas-alignment";

/** 移動対象 1 件の初期位置・サイズ（左上 + 幅高さ）。 */
export interface DragFrame {
  readonly baseLeft: number;
  readonly baseTop: number;
  readonly baseWidth: number;
  readonly baseHeight: number;
}

export interface ResolveDragInput {
  readonly frames: readonly DragFrame[];
  readonly rawDx: number;
  readonly rawDy: number;
  readonly staticBoxes: readonly AlignmentBox[];
  /** スナップ判定しきい値（ステージ px。通常は screen px / zoom）。 */
  readonly threshold: number;
  readonly gridSnapEnabled: boolean;
  /** グリッド吸着の丸めを適用するか（gridSnapEnabled と両方 true のときのみ丸める）。 */
  readonly applyGrid: boolean;
  /** 整列スナップ（スマートガイド）を有効にするか。回転オブジェクト移動時は false。 */
  readonly alignEnabled: boolean;
}

export interface ResolveDragOutput {
  /** frames と同順の確定（またはプレビュー）位置。 */
  readonly positions: readonly { readonly x: number; readonly y: number }[];
  readonly guides: readonly AlignmentGuide[];
}

export function resolveDragPositions(input: ResolveDragInput): ResolveDragOutput {
  const { frames, rawDx, rawDy, staticBoxes, threshold, gridSnapEnabled, applyGrid, alignEnabled } =
    input;

  // 移動量の有限性のみ担保する（当初仕様＝キャンバス外にも配置可へ戻したため、境界制約は行わない）。
  const base = sanitizeDelta(rawDx, rawDy);

  // 移動集合の外接矩形（base 適用後）を整列対象として扱う。回転を含む移動では整列を無効化する。
  const union = unionBox(frames, base.dx, base.dy);
  const alignment = alignEnabled
    ? computeAlignmentSnap(union, staticBoxes, threshold)
    : { snapDx: 0, snapDy: 0, matchedX: false, matchedY: false, guides: [] as AlignmentGuide[] };

  // グループ全体へ共通の差分として、整列スナップ + グリッド補正をまとめて適用する。
  let totalDx = base.dx + alignment.snapDx;
  let totalDy = base.dy + alignment.snapDy;
  if (applyGrid && gridSnapEnabled) {
    // 整列が成立した軸は整列を優先し、非成立軸のみ外接矩形の左上をグリッドへ揃える（相対配置を保つ）。
    const alignedUnionLeft = union.x + alignment.snapDx;
    const alignedUnionTop = union.y + alignment.snapDy;
    if (!alignment.matchedX) totalDx += snapToGrid(alignedUnionLeft) - alignedUnionLeft;
    if (!alignment.matchedY) totalDy += snapToGrid(alignedUnionTop) - alignedUnionTop;
  }

  // 補正後の共通差分を有限化するだけ（境界制約は行わない。グループの剛体性＝相対配置は totalDx/Dy を
  // 全フレームへ共通適用することで保たれる）。
  const clamped = sanitizeDelta(totalDx, totalDy);
  const positions = frames.map((frame) => ({
    x: frame.baseLeft + clamped.dx,
    y: frame.baseTop + clamped.dy,
  }));

  // 境界 clamp を廃したため、整列スナップの補助線はそのまま表示する（clamp による位置ずれが起きない）。
  return { positions, guides: alignment.guides };
}

/** 移動集合の外接矩形を求める（各フレームを dx,dy だけ動かした後の合併）。 */
function unionBox(frames: readonly DragFrame[], dx: number, dy: number): AlignmentBox {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const frame of frames) {
    const left = frame.baseLeft + dx;
    const top = frame.baseTop + dy;
    minX = Math.min(minX, left);
    minY = Math.min(minY, top);
    maxX = Math.max(maxX, left + frame.baseWidth);
    maxY = Math.max(maxY, top + frame.baseHeight);
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** 移動量の有限性のみを担保する（NaN/Infinity は 0 扱い）。当初仕様（キャンバス外にも配置可）へ
 *  戻したため、キャンバス境界での移動量制約は行わない。 */
function sanitizeDelta(dx: number, dy: number): { readonly dx: number; readonly dy: number } {
  return {
    dx: Number.isFinite(dx) ? dx : 0,
    dy: Number.isFinite(dy) ? dy : 0,
  };
}

function snapToGrid(value: number): number {
  return Math.round(value / CANVAS_SNAP_PX) * CANVAS_SNAP_PX;
}
